/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { Component, Input, inject, ChangeDetectionStrategy } from '@angular/core'
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms'
import { Observable } from 'rxjs'
import { Store } from '@ngxs/store'
import { ConfirmationSelectors } from '../../../state/confirmation/confirmation.selectors'
import { TranslateService } from '@ngx-translate/core'
import { Confirmation } from '../../../state/confirmation/confirmation.actions'
import { filter } from 'rxjs/operators'
import { ConfirmationCodeStatus } from '../../../enums/confirmation-code-status'
import { PidxGoogleTagManagerIntegrationService } from '../../../services/google-tag-manager/pidx-google-tag-manager-integration.service'
import { SignupService } from '../../../services/signup/signup.service'
import { TransactionType } from '../../../enums/transaction-type'
import ValidateOTP = Confirmation.ValidateOTP
import { isoToCompact, isoYearsAgo, todayIso } from './date-format.util'

@Component({
  selector: 'myprofile-signup-ui-dob-confirm-identity',
  templateUrl: './dob-confirm-identity.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DobConfirmIdentityComponent {
  private _translateService = inject(TranslateService)
  private store = inject(Store)
  private _tagManagerIntegrationService = inject(PidxGoogleTagManagerIntegrationService)
  private signupService = inject(SignupService)
  // we do not validate and show any min or max errors in the UI,
  // this will only be used to limit the datepicker selection
  protected maxDate = todayIso()
  protected minDate = isoYearsAgo(100)

  @Input() currentPageName!: string
  @Input() errorMessages!: string[]

  public readonly codeStatus$: Observable<ConfirmationCodeStatus> = inject(Store).select(ConfirmationSelectors.codeStatus)

  public dateOfBirthFormGroup: UntypedFormGroup = new UntypedFormGroup({
    dateOfBirth: new UntypedFormControl(''),
  })

  public ConfirmationCodeStatus = ConfirmationCodeStatus

  constructor() {
    this.codeStatus$.pipe(filter((status) => status === ConfirmationCodeStatus.CODE_STATUS_INVALID)).subscribe((status) => {
      const contextDependentError = [TransactionType.SECONDARY, TransactionType.SECONDARY_VEHICLE].includes(
        this.signupService.transactionType,
      )
        ? 'signup.generic.error.dateOfBirth.confirmationCodeIsInvalidSecondary'
        : 'signup.generic.error.dateOfBirth.confirmationCodeIsInvalid'
      this.errorMessages = [this._translateService.instant(contextDependentError)]
      this._tagManagerIntegrationService.triggerLoadTracking({
        pageName: 'sign_up/identityverification',
        eventAction: 'PAGMyPorsche_SignUpIdentityVerificationError_Load',
      })
    })
  }

  get dateOfBirthFormControlErrorMessage() {
    return this.errorMessages?.[0]
  }

  onSubmitOTP() {
    if (!this.dateOfBirthFormGroup.get('dateOfBirth')!.value?.length) return
    const dateOfBirth = this.dateOfBirthFormGroup.controls.dateOfBirth.value
    const confirmationCode = this.formatDateOfBirth(dateOfBirth)
    this.store.dispatch(new ValidateOTP(confirmationCode))
  }

  private formatDateOfBirth(dateOfBirth: string) {
    return isoToCompact(dateOfBirth)
  }
}
